/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import React, { useEffect, useMemo, useState } from "react";
import { replaceBr, EventInfo } from "../../../utility";
import { useSelector } from "react-redux";
import { Store } from "store";
import { changeStyleWithHosting } from "utility/hosting";
import { useI18n, i18nextT, I18nTrans } from "hooks/i18n/i18n";
import { isAllowedReferrer, IS_FREE_PURCHASE } from "utility/ticket";
import type { Place, TicketConfig } from "@spwn/types/firebase/firestore";
import { sanitizer } from "utility/sanitize";
import { TextLink } from "ui/TextLink";
import { useFeatureFlagFromFirestore } from "../../streaming/useFeatureFlagFromFirestore";
import { preprocessTextForWeglotTranslation } from "i18n/textTransformations";
import { withNoTranslate } from "i18n/noTranslate";
import { TRANSLATE_THIS_BLOCK_BY_WEGLOT } from "i18n/weglot";
import type { PurchaseTicketData } from "modules/ticket";

interface Props {
  displayTickets: PurchaseTicketData[];
  selectedPlaceCode: string;
  placeMap: Record<string, Place>;
  ticketConfig: TicketConfig;
  event: EventInfo;
  displaySubClass: (value: string) => void;
  selectTicket: (
    e: React.ChangeEvent<HTMLSelectElement>,
    ticketId: string
  ) => void;
  handleSelectorChange: (
    e: React.ChangeEvent<HTMLSelectElement>,
    ticket: PurchaseTicketData
  ) => void;
}

const EventTicketSelectNum: React.FC<Props> = (props: Props) => {
  const PURCHASE_MAX_LIMIT = 99;

  const { t } = useI18n();
  const isAddressRegistered = useSelector(
    (state: Store) => state.auth.isAddressRegistered
  );
  const freePurchaseEnabled = useFeatureFlagFromFirestore(
    "freePurchase",
    props.event.eventId
  );
  const classesEventTicketSelectNumDesign = eventTicketSelectNumDesign();

  const [displayTickets, setDisplayTickets] = useState<PurchaseTicketData[]>(
    []
  );

  useEffect(() => {
    (async () => {
      // SEE(takahashi): https://qiita.com/daishi/items/4423878a1cd7a0ab69eb
      const bits = await Promise.all(
        props.displayTickets.map(async (ticket) =>
          isAllowedReferrer(ticket._id, document.referrer)
        )
      );
      setDisplayTickets(props.displayTickets.filter((_, i) => bits[i]));
    })();
  }, [props.displayTickets]);

  return (
    <div
      css={classesEventTicketSelectNumDesign.root}
      key={props.selectedPlaceCode}
    >
      <h3 css={classesEventTicketSelectNumDesign.heading}>
        {t("event.ticket.headline.ticket", { selectTarget: "チケットの枚数" })}
      </h3>

      <div css={classesEventTicketSelectNumDesign.ticketLayout}>
        {displayTickets.map((ticket, i) => {
          const place = props.placeMap[props.selectedPlaceCode];
          const isPurchased = ticket.remaining === 0;
          const isSoldOut = ticket.remaining === -1;
          const isFree =
            Number(ticket.price_jpy) <= 0 &&
            // 特定のドメインやイベントでは購入できるようにする
            !IS_FREE_PURCHASE() &&
            !freePurchaseEnabled;

          let sellStatus = "";
          if (!props.event.isLotteryTicketSellingMode && !isFree) {
            if (isPurchased) {
              sellStatus = "purchased";
            } else if (isSoldOut) {
              sellStatus = "sold_out";
            }
          }

          const alertInfo = ""; // eg. 限定21席 TODO@later

          return (
            <div
              className={sellStatus}
              css={classesEventTicketSelectNumDesign.ticketItem}
              key={i}
            >
              <div css={classesEventTicketSelectNumDesign.ticketItemText}>
                <div className="layout_l">
                  <p className="place">
                    {/* eslint-disable no-irregular-whitespace */}
                    {place && place.areaName}　{alertInfo}
                    {/* eslint-enable */}
                  </p>

                  <h4 className="name">
                    {preprocessTextForWeglotTranslation(replaceBr(ticket.name))}
                  </h4>
                  {ticket.saleType !== "voucherOnly" && (
                    <p {...withNoTranslate({ className: "price" })}>
                      ¥{ticket.price_jpy.toLocaleString()}{" "}
                      {isFree ? i18nextT("pawCharge.freeTicket") : ""}
                    </p>
                  )}
                </div>
                {isFree ||
                ((ticket.isAddressRequired || ticket.isAddressRequiredNoFee) &&
                  !isAddressRegistered) ? (
                  <React.Fragment></React.Fragment>
                ) : (
                  <div className="layout_r">
                    {props.ticketConfig.hasSubClass &&
                    ticket.subClassName &&
                    ticket.subClassName.length > 0 ? (
                      <TicketCountSelect
                        ticket={ticket}
                        ticketConfig={props.ticketConfig}
                        isLotteryTicketSellingMode={
                          props.event.isLotteryTicketSellingMode
                        }
                        purchaseMaxLimit={PURCHASE_MAX_LIMIT}
                        onChange={(e) => {
                          props.displaySubClass(e.target.value);
                        }}
                      />
                    ) : (
                      <TicketCountSelect
                        ticket={ticket}
                        ticketConfig={props.ticketConfig}
                        isLotteryTicketSellingMode={
                          props.event.isLotteryTicketSellingMode
                        }
                        purchaseMaxLimit={PURCHASE_MAX_LIMIT}
                        onChange={(e) => {
                          props.handleSelectorChange(e, ticket); // check and add lottery cart
                          props.selectTicket(e, ticket._id); // only add to cart
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
              <AddressRequiredNotice
                isAddressRequired={ticket.isAddressRequired ?? false}
                isAddressRequiredNoFee={ticket.isAddressRequiredNoFee ?? false}
                isAddressRegistered={isAddressRegistered}
              />

              {ticket.description ? (
                <div
                  className={TRANSLATE_THIS_BLOCK_BY_WEGLOT}
                  css={classesEventTicketSelectNumDesign.ticketItemBenefits}
                  dangerouslySetInnerHTML={{
                    __html: sanitizer(
                      preprocessTextForWeglotTranslation(ticket.description)
                    ),
                  }}
                ></div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default EventTicketSelectNum;

type TicketCountSelectProps = {
  ticket: PurchaseTicketData;
  ticketConfig: TicketConfig;
  isLotteryTicketSellingMode: boolean;
  purchaseMaxLimit: number;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

const TicketCountSelect: React.FC<TicketCountSelectProps> = ({
  ticket,
  ticketConfig,
  isLotteryTicketSellingMode,
  purchaseMaxLimit,
  onChange,
}) => {
  let n_ticket = Math.min(
    ticket.purchaseLimit,
    ticketConfig.config?.maxTicketPurchase ?? purchaseMaxLimit
  );
  /**
   * n_ticketが99999など極端に大きい数値がループに渡されないように
   * purchaseMaxLimitより大きい場合は、n_ticketにpurchaseMaxLimitが入るようにする
   */
  n_ticket = Math.min(n_ticket, purchaseMaxLimit);

  if (!isLotteryTicketSellingMode) {
    n_ticket = Math.min(n_ticket, ticket.remaining);
  }

  const countOptions = [];
  for (let i = 0; i <= n_ticket; i++) {
    if (
      ticketConfig.hasSubClass &&
      ticket.subClassName &&
      ticket.subClassName.length > 0
    ) {
      countOptions.push(
        <option key={i} value={i + "_" + ticket.classGroupId}>
          {i}
        </option>
      );
    } else {
      countOptions.push(
        <option id={ticket._id} key={i} value={i}>
          {i}
        </option>
      );
    }
  }

  return (
    <select defaultValue={"0"} onChange={onChange}>
      {countOptions}
    </select>
  );
};

type AddressRequiredNoticeProps = {
  isAddressRequired: boolean;
  isAddressRequiredNoFee: boolean;
  isAddressRegistered: boolean;
};
const AddressRequiredNotice = (props: AddressRequiredNoticeProps) => {
  // const { t } = useI18n();
  const classes = useMemo(() => {
    return css`
      padding: 0 15px 15px;
      font-size: 12px;
      font-weight: bold;
      line-height: 1.5em;
      letter-spacing: 1px;
    `;
  }, []);

  // add notice
  if (props.isAddressRequired || props.isAddressRequiredNoFee) {
    return (
      <p css={classes}>
        <I18nTrans i18nKey="event.ticket.requiredNotice">
          このチケットを購入するには
          <TextLink href={"/account/address"}>こちら</TextLink>
          で住所登録が必要です。
        </I18nTrans>
      </p>
    );
  }
  return null;
};

const eventTicketSelectNumDesign = () => {
  const hostingBaseStyleMap = changeStyleWithHosting().baseStyles;
  return {
    root: css``,
    heading: css`
      margin-bottom: 25px;
      display: flex;
      font-size: 18px;
      @media screen and (min-width: 768px) {
        margin-bottom: 40px;
        font-size: 25px;
      }
      &::before {
        content: "2";
        display: block;
        margin-right: 5%;
        font-family: "din-condensed", sans-serif;
        font-weight: 400;
        font-style: normal;
        font-size: 35px;
        @media screen and (min-width: 768px) {
          margin-right: 20px;
          font-size: 43px;
        }
      }
    `,
    ticketLayout: css`
      @media screen and (min-width: 768px) {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;
      }
      &::after {
        @media screen and (min-width: 768px) {
          content: "";
          display: block;
          width: 305px;
        }
      }
    `,
    ticketItem: css`
      position: relative;
      margin-bottom: 25px;
      border: 1px solid #d2d2d2;
      @media screen and (min-width: 768px) {
        width: 305px;
      }
      &.purchased::after,
      &.sold_out::after {
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        font-family: "din-condensed", sans-serif;
        color: #fff;
        text-align: center;
        font-weight: 400;
        font-style: normal;
        background-color: rgba(0, 0, 0, 0.5);
      }
      &.purchased::after {
        content: "";
        font-size: 30px;
      }
      &.sold_out::after {
        content: "SOLD OUT";
        font-size: 35px;
      }
    `,
    ticketItemText: css`
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding: 15px 15px 20px;
      .layout_l {
        width: calc(100% - 90px);
        margin-right: 15px;
        p.place {
          margin-bottom: 8px;
          color: ${hostingBaseStyleMap.accentColor};
          font-size: 11px;
          font-weight: bold;
          @media screen and (min-width: 768px) {
            font-size: 14px;
          }
        }
        p.price {
          margin-top: 12px;
          font-weight: bold;
        }
      }
      .layout_r {
        width: 75px;
      }
    `,
    ticketItemBenefits: css`
      padding: 15px;
      font-size: 10px;
      background-color: ${hostingBaseStyleMap.accentColor}20;
      li {
        padding-left: 1em;
        text-indent: -1em;
        line-height: 1.5em;
      }
      a {
        color: #07ccd5;
        text-decoration: underline;
      }
    `,
  };
};
